import React from "react";
import { useFormContext } from "react-hook-form";
import { Box, Typography, Grid, Divider } from "@mui/material";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import { removeTrailZero } from "@/utils/numberFormatting";
import { formatManufacturerName } from "@/utils/formatManufacturerName";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";

import FormInput from "@/components/FormInput";

const ParticipantEnrollmentStep = () => {
  const { watch, control } = useFormContext();

  // Map external incentive program values to user-friendly labels
  const getExternalProgramLabel = (value: string) => {
    const valueLabelMap: Record<string, string> = {
      "dac-sash": "DAC-SASH",
      sgip: "SGIP",
      "city-solar-equity": "City of San Diego's Solar Equity Program",
      other: "Other",
      none: "None",
    };
    return valueLabelMap[value] || value;
  };

  const customerName = watch("customerName") || "";
  const accountHash = watch("accountHash") || "";
  const accountEmail = watch("accountEmail") || "";
  const streetAddress = watch("streetAddress") || "";
  const city = watch("city") || "";
  const state = watch("state") || "";
  const zipCode = watch("zipCode") || "";

  const solarSystemType = watch("solarSystemType") || [];
  const existingSolarKwdc = watch("solarKwdc") || "";
  const existingSolarKwac = watch("solarKwac") || "";
  const existingSolarAnnualProduction = watch("solarAnnualProduction") || "";
  const newSolarKwdc = watch("newSolarKwdc") || "";
  const newSolarKwac = watch("newSolarKwac") || "";
  const newSolarAnnualProduction = watch("newSolarAnnualProduction") || "";
  const inverterManufacturer = watch("inverterManufacturer") || "";
  const inverterName = watch("inverterName") || "";

  const batteryManufacturer = watch("batteryManufacturer") || "";
  const batteryProductName = watch("batteryProductName") || "";
  const batteryQuantity = watch("batteryQuantity") || "";

  const upfrontIncentivePayment = Number(watch("upfrontIncentivePayment")) || 0;
  const externalIncentivePrograms = watch("externalIncentivePrograms") || "";
  const externalIncentives = Number(watch("externalIncentives")) || 0;

  const payeeType = watch("payeeType") || "";
  const payeeName = watch("payeeName") || "";
  const payeeAddress = watch("payeeAddress") || "";
  const payeeCity = watch("payeeCity") || "";
  const payeeState = watch("payeeState") || "";
  const payeeZip = watch("payeeZip") || "";

  const hasExistingSolar =
    Array.isArray(solarSystemType) && solarSystemType.includes("existing");
  const hasNewSolar =
    Array.isArray(solarSystemType) && solarSystemType.includes("new");

  const SummarySection = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 500,
          color: "#373533",
          marginBottom: "12px",
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Box>
  );

  return (
    <Box sx={{ maxHeight: "500px", overflowY: "auto", marginTop: "8px" }}>
      <SummarySection title="Account Information">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormInput
              name="customerName"
              control={control}
              label="Account Name"
              value={customerName}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="accountHash"
              control={control}
              label="Account Number"
              value={accountHash}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormInput
              name="serviceAddress"
              control={control}
              label="Service Address"
              value={`${streetAddress}, ${city}, ${state}, ${zipCode}`}
              disabled
            />
          </Grid>
        </Grid>
      </SummarySection>

      {hasExistingSolar && (
        <SummarySection title="Existing Solar System Details">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormInput
                name="solarKwdc"
                control={control}
                label="Existing Solar System: kW-DC"
                value={`${removeTrailZero(existingSolarKwdc)} kW`}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInput
                name="solarKwac"
                control={control}
                label="Existing Solar System: kW-AC"
                value={`${removeTrailZero(existingSolarKwac)} kW`}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                name="solarAnnualProduction"
                control={control}
                label="Estimated Annual Solar Production (kWh)"
                value={`${removeTrailZero(existingSolarAnnualProduction)} kWh`}
                disabled
              />
            </Grid>
          </Grid>
        </SummarySection>
      )}

      {hasNewSolar && (
        <SummarySection title="New Solar System Details">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormInput
                name="newSolarKwdc"
                control={control}
                label="New Solar System: kW-DC"
                value={`${removeTrailZero(newSolarKwdc)} kW`}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInput
                name="newSolarKwac"
                control={control}
                label="New Solar System: kW-AC"
                value={`${removeTrailZero(newSolarKwac)} kW`}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                name="newSolarAnnualProduction"
                control={control}
                label="Estimated Annual Solar Production (kWh)"
                value={`${removeTrailZero(newSolarAnnualProduction)} kWh`}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInput
                name="inverterManufacturer"
                control={control}
                label="Inverter Manufacturer"
                value={formatManufacturerName(inverterManufacturer)}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormInput
                name="inverterName"
                control={control}
                label="Inverter Name"
                value={formatManufacturerName(inverterName)}
                disabled
              />
            </Grid>
          </Grid>
        </SummarySection>
      )}

      <SummarySection title="Battery System Details">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormInput
              name="batteryManufacturer"
              control={control}
              label="Battery Manufacturer *"
              value={batteryManufacturer}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="batteryProductName"
              control={control}
              label="Battery Product Name *"
              value={batteryProductName}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="batteryQuantity"
              control={control}
              label="Battery Quantity"
              value={batteryQuantity}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="usableCapacity"
              control={control}
              label="Total Capacity (kWh)"
              value={`${removeTrailZero(watch("totalCapacity") || 0)} kWh`}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="nominalPower"
              control={control}
              label="Nominal Power (kW)"
              value={`${removeTrailZero(watch("nominalPower") || 0)} kW`}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="totalNominalPower"
              control={control}
              label="Total Nominal Power (kW)"
              value={`${removeTrailZero(watch("totalNominalPower") || 0)} kW`}
              disabled
            />
          </Grid>
        </Grid>
      </SummarySection>

      <SummarySection title="Incentive Information">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormInput
              name="upfrontIncentivePayment"
              control={control}
              label="Upfront Incentive Amount"
              value={formatCurrency(upfrontIncentivePayment)}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="externalIncentivePrograms"
              control={control}
              label="External Programs"
              value={getExternalProgramLabel(
                watch("externalIncentivePrograms") || ""
              )}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              name="externalIncentives"
              control={control}
              label="External Incentives"
              value={formatCurrency(externalIncentives)}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              name="thirdPartyOwned
"
              control={control}
              label="Third-Party Ownership"
              value={watch("thirdPartyOwned") ? "Yes" : "No"}
              disabled
            />
          </Grid>
        </Grid>
      </SummarySection>

      <SummarySection title="Payee Information Details">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormInput
              name="payeeType"
              control={control}
              label="Payee Type"
              value={capitalizeFirstLetter(payeeType)}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              name="payeeName"
              control={control}
              label="Payee Name"
              value={payeeName}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              name="payeeAddress"
              control={control}
              label="Mailing Address"
              value={`${payeeAddress}, ${payeeCity}, ${payeeState}, ${payeeZip}`}
              disabled
            />
          </Grid>
        </Grid>
      </SummarySection>
    </Box>
  );
};

export default ParticipantEnrollmentStep;
